import {
  redirect,
  type ActionFunction,
  type LoaderFunction,
  type V2_MetaFunction,
} from "@remix-run/cloudflare";
import { getAuth } from "@clerk/remix/ssr.server";
import { createDb } from "~/db/db-client.server";
import type { Job, JobActions } from "~/db/types";
import type { Env } from "./_index";
import { jobs } from "~/db/schema/jobs";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Link,
  isRouteErrorResponse,
  useLoaderData,
  useNavigate,
  useRouteError,
  useSearchParams,
} from "@remix-run/react";
import { eq, and, gte, desc, sql, like, or } from "drizzle-orm";
import { jobActiveCreationDate } from "~/utils/date-utils";

import SearchMenu from "~/components/searchMenu";
import { candidates } from "~/db/schema/candidates";
import { savedjobs } from "~/db/schema/savedjobs";
import { redirectWithToast } from "~/utils/toast.server";
import { isNumeric } from "~/utils/utils";
import { appliedjobs } from "~/db/schema/appliedjobs";
import { JobArticle } from "~/components/jobArticle";
import { JOBS_PER_PAGE } from "~/utils/consts";
import { Button } from "~/components/ui/button";

export const meta: V2_MetaFunction = () => {
  return [
    {
      title:
        "Schools Jobs | India's 1st Privacy focused job portal dedicated for educational institutions.",
    },
    { name: "description", content: "Welcome to Schooljobs.live" },
  ];
};

export const action: ActionFunction = async ({ request, context, params }) => {
  const { userId } = await getAuth(
    { request, context, params },
    {
      secretKey:
        context.env && (context.env as Env)["CLERK_SECRET_KEY"]
          ? (context.env as Env)["CLERK_SECRET_KEY"]
          : "",
    }
  );

  if (!userId) {
    return redirect("/sign-in");
  }

  const formData = await request.formData();

  const jobIdValue = formData.get("jobId");

  const jobId = isNumeric(jobIdValue) ? Number(jobIdValue) : null;

  if (!jobId) {
    return new Response("Not valid jobId", { status: 422 });
  }

  const db = createDb(context);

  const activeCandidateData = await db
    .select()
    .from(candidates)
    .where(eq(candidates.clerkUserId, userId))
    .get();

  if (userId && !activeCandidateData) {
    return redirectWithToast("../../candidate/profile", {
      title: "Please complete your profile",
      description: "Complete your profile before save/apply to a job.",
      type: "message",
    });
  }

  if (activeCandidateData && !activeCandidateData.id) {
    return redirectWithToast("../../candidate/profile", {
      title: "Please complete your profile",
      description: "Complete your profile before save/apply to a job.",
      type: "message",
    });
  }

  const candidateId = activeCandidateData.id;

  try {
    if (formData.get("_intent") === "save") {
      const savedJob = await db
        .insert(savedjobs)
        .values({ jobId, candidateId })
        .returning()
        .get();
      console.log("Job inserted into saved jobs", savedJob);
      return { savedJob };
    } else if (formData.get("_intent") === "remove") {
      const deletedJob = await db
        .delete(savedjobs)
        .where(eq(savedjobs.jobId, jobId))
        .returning()
        .get();
      console.log("Job removed from saved jobs", deletedJob);
      return { deletedJob };
    }
  } catch (error) {
    console.log("Error saving or removing saved job into DB", error);
    return redirectWithToast(".", {
      title: "Uh oh! Something went wrong.",
      description: "There was a problem with your request. Please try again.",
      type: "error",
    });
  }

  return null;
};

export const loader: LoaderFunction = async ({ request, context, params }) => {
  const { userId } = await getAuth(
    { request, context, params },
    {
      secretKey:
        context.env && (context.env as Env)["CLERK_SECRET_KEY"]
          ? (context.env as Env)["CLERK_SECRET_KEY"]
          : "",
    }
  );

  const url = new URL(request.url);
  const title = url.searchParams.get("t") ?? "";
  const location = url.searchParams.get("l") ?? "";
  const page =
    url.searchParams.get("p") && isNumeric(url.searchParams.get("p"))
      ? Number(url.searchParams.get("p"))
      : 1;

  const db = createDb(context);

  let activeCandidateData;

  if (userId) {
    activeCandidateData = await db
      .select()
      .from(candidates)
      .where(eq(candidates.clerkUserId, userId))
      .get();
  }

  // if (userId && !activeCandidateData) {
  //   return new Response("Not valid candidate", { status: 422 });
  // }

  // if (activeCandidateData && !activeCandidateData.id) {
  //   return new Response("Not valid candidate", { status: 422 });
  // }

  const activeJobsDataPromise = db
    .select({
      id: jobs.id,
      title: jobs.title,
      location: jobs.location,
      salary: jobs.salary,
      state: jobs.state,
      country: jobs.country,
      postalCode: jobs.postalCode,
      contractType: jobs.contractType,
      workingHours: jobs.workingHours,
      shortDescription: jobs.shortDescription,
      currency: jobs.currency,
      salaryTimePeriod: jobs.salaryTimePeriod,
      schoolId: jobs.schoolId,
      schoolName: jobs.schoolName,
      postedAt: jobs.postedAt,
      createdAt: jobs.createdAt,
      saved: sql<boolean>`${db
        .select({ n: savedjobs.createdAt })
        .from(savedjobs)
        .where(
          and(
            eq(savedjobs.jobId, jobs.id),
            eq(
              savedjobs.candidateId,
              activeCandidateData ? activeCandidateData.id : 0
            )
          )
        )}`,
      applied: sql<boolean>`${db
        .select({ n: appliedjobs.createdAt })
        .from(appliedjobs)
        .where(
          and(
            eq(appliedjobs.jobId, jobs.id),
            eq(
              appliedjobs.candidateId,
              activeCandidateData ? activeCandidateData.id : 0
            )
          )
        )}`,
    })
    .from(jobs)
    .where(
      and(
        gte(jobs.postedAt, jobActiveCreationDate()),
        eq(jobs.suspended, false),
        like(jobs.title, `%${title}%`),
        or(
          like(jobs.location, `%${location}%`),
          like(jobs.state, `%${location}%`)
        )
      )
    )
    .orderBy(desc(jobs.postedAt), desc(jobs.createdAt))
    .offset((page - 1) * JOBS_PER_PAGE)
    .limit(JOBS_PER_PAGE)
    .all();

  const totalCountPromise = db
    .select({ count: sql<number>`count(*)` })
    .from(jobs)
    .where(
      and(
        gte(jobs.postedAt, jobActiveCreationDate()),
        eq(jobs.suspended, false),
        like(jobs.title, `%${title}%`),
        or(
          like(jobs.location, `%${location}%`),
          like(jobs.state, `%${location}%`)
        )
      )
    )
    .all();

  const [activeJobsData, totalCount] = await Promise.all([
    activeJobsDataPromise,
    totalCountPromise,
  ]);

  return {
    activeJobsData,
    title,
    location,
    page: Number(page),
    totalCount: totalCount[0].count,
    totalPages: Math.ceil(totalCount[0].count / JOBS_PER_PAGE),
  };
};

export default function SearchJobs() {
  const { activeJobsData, title, location, totalCount, totalPages, page } =
    useLoaderData<typeof loader>() as {
      activeJobsData: (Job & JobActions)[];
      title: string;
      location: string;
      totalCount: number;
      totalPages: number;
      page: number;
    };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getNextPageUrl = () => {
    if (page >= totalPages) return "";
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("p", String(page + 1));
    return "../search/jobs?" + newSearchParams.toString();
  };

  const getPrevPageUrl = () => {
    if (page === 1) return "";
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("p", String(page - 1));
    return "../search/jobs?" + newSearchParams.toString();
  };

  console.log("totalPages", totalPages);
  console.log("totalCount", totalCount);
  console.log("page", page);

  return (
    <div className="flex -mx-4 sm:m-0">
      <div className="hidden md:block flex-[2_2_0%]">
        <SearchMenu isMobile={false} />
      </div>
      <div className="flex-[4_4_0%]">
        <header className="sm:mb-4 flex justify-between px-4">
          <h1 className="text-base font-bold">{`${
            title === "" ? "" : title
          } Jobs in ${location === "" ? "all" : location} location`}</h1>
          <p className="font-bold text-base">{totalCount} jobs</p>
        </header>

        <ul className="border-t border-lime-400 mt-5">
          {activeJobsData.length !== 0 ? (
            <>
              <div>
                {activeJobsData.map((job) => (
                  <li
                    key={job.id + "job-id"}
                    className="border-b border-slate-300 hover:border-lime-400 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      navigate(`../job/${job.id}`);
                    }}
                  >
                    <JobArticle job={job} />
                  </li>
                ))}
                <div className="grid w-full grid-cols-2 mt-8">
                  <div>
                    {getPrevPageUrl() !== "" ? (
                      <Link to={getPrevPageUrl()} className="flex gap-2">
                        <Button
                          className="flex gap-2"
                          variant={"outline"}
                          size={"lg"}
                        >
                          <ArrowLeft className="text-lime-400" /> Previous
                        </Button>
                      </Link>
                    ) : null}
                  </div>

                  <div className="ml-auto">
                    {getNextPageUrl() !== "" ? (
                      <Link to={getNextPageUrl()} className="flex gap-2">
                        <Button
                          className="flex gap-2"
                          variant={"outline"}
                          size={"lg"}
                        >
                          Next <ArrowRight className="text-lime-400" />
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <li className="flex flex-col justify-center items-center h-32 sm:h-64 px-4 py-6">
              <p className="text-2xl capitalize">{`No ${
                title === "" ? "" : title
              } jobs found in ${location === "" ? "all" : location}`}</p>

              <p className="p-6">
                <Link to={"../search/jobs"} className="flex gap-2">
                  View all Jobs
                  <ArrowRight className="text-lime-400" />
                </Link>
              </p>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>Oops</h1>
        <p>Status: {error.status}</p>
        <p>{error.data}</p>
      </div>
    );
  } else {
    return (
      <div>
        <h1>Uh oh ...</h1>
        <p>Something went wrong.</p>
        <pre>{error.data}</pre>
      </div>
    );
  }
}
